<!--
  FileThumb.vue

  A file's preview tile, by kind: the image itself, or a tinted tile with the
  kind's icon, its extension and, for audio and video, its length. Images
  larger than `maxSize` stay a tile so a gallery never pulls a 40MB original.
-->
<template>
  <div
    class="file-thumb"
    :class="[`is-${kind}`, { checkerboard: showImage && loaded, 'is-compact': compact }]"
    :style="style"
  >
    <img
      v-if="showImage"
      :class="{ 'is-loading': !loaded }"
      :src="src ?? undefined"
      :alt="asset.name || 'Preview'"
      loading="lazy"
      decoding="async"
      @load="loaded = true"
      @error="failed = true"
    />
    <template v-if="!showImage || !loaded">
      <span class="icon"
        ><font-awesome-icon :icon="icon" :size="compact ? undefined : 'lg'"
      /></span>
      <span v-if="!compact" class="ext">{{ badge }}</span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { assetKind } from "@auger/core/fields"
import type { AssetFieldsFragment } from "@auger/core/gql/types"
import { formatDuration } from "@auger/ui/utils/number"
import { assetSrc, fileIcon } from "@/composables/useAssets"

const props = withDefaults(
  defineProps<{
    asset: Pick<AssetFieldsFragment, "name" | "mime_type" | "url"> &
      Partial<Pick<AssetFieldsFragment, "size" | "metadata">>
    /** Pixel height; width fills the parent. */
    height?: number
    /** Pixel side of a square thumb, for chips and rows. Overrides height. */
    square?: number
    /** No inline size: the caller's CSS sizes it. */
    fluid?: boolean
    /** Icon only, no extension badge. Defaults on for fluid and under 48px. */
    compact?: boolean
    /** Images over this many bytes show as a tile. */
    maxSize?: number
  }>(),
  { height: 120, square: undefined, fluid: false, compact: undefined, maxSize: 5 * 1024 * 1024 }
)

// Until the image arrives (lazy, and slow through the dev proxy) the kind's
// icon stands in, so a loading image never reads as an empty one.
const failed = ref(false)
const loaded = ref(false)
watch(
  () => props.asset.url,
  () => {
    failed.value = false
    loaded.value = false
  }
)

const kind = computed(() => assetKind(props.asset.mime_type))
const icon = computed(() => fileIcon(props.asset.mime_type))
const src = computed(() => assetSrc(props.asset))
const compact = computed(
  () => props.compact ?? (props.fluid || (props.square ?? props.height) < 48)
)

const showImage = computed(
  () =>
    kind.value === "image" &&
    !!src.value &&
    !failed.value &&
    (props.asset.size ?? 0) <= props.maxSize
)

const extension = computed(() => {
  const match = /\.([a-z0-9]{1,5})$/i.exec(props.asset.name ?? "")
  return match ? match[1]!.toUpperCase() : kind.value.toUpperCase()
})

const badge = computed(() => {
  const duration = (props.asset.metadata as { duration?: number } | null)?.duration
  return duration ? `${extension.value} · ${formatDuration(duration)}` : extension.value
})

const style = computed(() =>
  props.fluid
    ? undefined
    : props.square
      ? { width: `${props.square}px`, height: `${props.square}px` }
      : { height: `${props.height}px` }
)
</script>

<style>
.file-thumb {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  position: relative;
  flex: none;
  width: 100%;
  overflow: hidden;
  border-radius: var(--auger-radius);
  background: var(--auger-bg-muted);
  color: var(--auger-text-weak);

  /* Behind the image, so it shows only through transparent pixels. Mid-tone
     squares: the website's logos are white artwork, which vanishes on a light
     checkerboard, and dark artwork vanishes on a dark one. */
  &.checkerboard {
    background: repeating-conic-gradient(
      var(--auger-steel-gray-light) 0% 25%,
      var(--auger-steel-gray-dark) 0% 50%
    );
    background-size: 16px 16px;

    &.is-compact {
      background-size: 6px 6px;
    }
  }

  /* The image fills the tile. */
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;

    /* Unseen until it arrives, but filling the tile: a lazy image with no box
       of its own (0×0) is never fetched. */
    &.is-loading {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
    }
  }

  .ext {
    font-family: var(--auger-font-mono);
    font-size: 0.7rem;
    letter-spacing: 0.02em;
  }

  &.is-pdf {
    background: var(--auger-danger-light);
    color: var(--auger-danger);
  }

  &.is-spreadsheet {
    background: var(--auger-success-light);
    color: var(--auger-success);
  }

  &.is-document,
  &.is-presentation {
    background: var(--auger-link-light);
    color: var(--auger-link);
  }

  &.is-audio {
    background: var(--auger-primary-soft);
    color: var(--auger-primary-dark);
  }

  &.is-video {
    background: var(--auger-steel-gray-darker);
    color: var(--auger-text-inverted);
  }
}
</style>
